/**
 * Admin — Class Rosters
 *
 * Teacher provisions a class once (first name + roll only, no PII/login). The
 * per-class join code goes in a test link (?class=<code>); learners pick their
 * name in the runner, so assessment results show up named in the module's
 * Analytics → Assessments tab.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trash2, Plus, Copy, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  adminListClasses,
  adminCreateClass,
  adminGetClass,
  adminAddLearners,
  adminDeleteLearner,
  adminDeleteClass,
  fetchCatalog,
  fetchAssessmentTokens,
  type SchoolClass,
  type Learner,
  type CatalogItem,
} from '../../services/learnAPI';

export const Route = createFileRoute('/admin/classes')({ component: AdminClasses });

const RUNNER = 'https://learn.4klabs.ai/assessment-runner.html';

function AdminClasses() {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [sel, setSel] = useState<(SchoolClass & { learners: Learner[] }) | null>(null);
  const [name, setName] = useState('');
  const [grade, setGrade] = useState('');

  const refresh = () => adminListClasses().then(setClasses).catch(() => {});
  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    await adminCreateClass({ name: name.trim(), grade: grade.trim() || undefined });
    setName('');
    setGrade('');
    refresh();
  };
  const open = async (id: string) => setSel(await adminGetClass(id));
  const del = async (id: string) => {
    if (confirm('Delete this class and its roster?')) {
      await adminDeleteClass(id);
      setSel(null);
      refresh();
    }
  };

  if (sel)
    return (
      <ClassDetail
        cls={sel}
        onBack={() => {
          setSel(null);
          refresh();
        }}
        reload={() => open(sel.id)}
      />
    );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Class Rosters</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Provision a class once; learners pick their name when they take a test, so the dashboard shows real students.
          First name + roll only — no login, no PII.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New class</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Class name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Class 10 A" />
          </div>
          <div>
            <Label>Grade (optional)</Label>
            <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Class 10" />
          </div>
          <Button onClick={create}>
            <Plus className="mr-1 h-4 w-4" />
            Create
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Classes</CardTitle>
        </CardHeader>
        <CardContent>
          {classes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No classes yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Join code</TableHead>
                  <TableHead className="text-right">Learners</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => open(c.id)}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.grade ?? '—'}</TableCell>
                    <TableCell className="font-mono">{c.joinCode}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.learnerCount ?? 0}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => del(c.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ClassDetail({
  cls,
  onBack,
  reload,
}: {
  cls: SchoolClass & { learners: Learner[] };
  onBack: () => void;
  reload: () => void;
}) {
  const [raw, setRaw] = useState('');
  const [modules, setModules] = useState<CatalogItem[]>([]);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([fetchCatalog(), fetchAssessmentTokens()])
      .then(([cat, toks]) => {
        const withTest = cat.filter((c) => toks.has(c.shareToken));
        setModules(withTest);
        if (withTest[0]) setToken(withTest[0].shareToken);
      })
      .catch(() => {});
  }, []);

  const add = async () => {
    if (!raw.trim()) return;
    await adminAddLearners(cls.id, raw);
    setRaw('');
    reload();
  };
  const removeLearner = async (lid: string) => {
    await adminDeleteLearner(cls.id, lid);
    reload();
  };
  const link = token ? `${RUNNER}?token=${token}&class=${cls.joinCode}` : '';
  const copy = () => {
    if (link) {
      navigator.clipboard?.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <Button variant="ghost" onClick={onBack}>
        <ChevronLeft className="mr-1 h-4 w-4" />
        All classes
      </Button>
      <div>
        <h1 className="text-2xl font-semibold">{cls.name}</h1>
        <p className="text-sm text-muted-foreground">
          {cls.grade ?? ''} · join code <span className="font-mono">{cls.joinCode}</span> · {cls.learners.length} learners
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Share a test with this class</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px]">
              <Label>Module</Label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              >
                {modules.map((m) => (
                  <option key={m.shareToken} value={m.shareToken}>
                    {m.title ?? m.shareToken}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={copy} disabled={!link}>
              <Copy className="mr-1 h-4 w-4" />
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
          </div>
          {link && <code className="block break-all rounded-md bg-muted p-3 text-xs">{link}</code>}
          <p className="text-xs text-muted-foreground">
            Give this link to the class. Each learner taps their name, then takes the test — their results show up named in
            the module&apos;s Analytics → Assessments tab.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add learners</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label>
            One per line — “roll firstName” (e.g. <span className="font-mono">1 Aarav</span>). Roll optional; blanks
            auto-number.
          </Label>
          <textarea
            className="h-32 w-full rounded-md border bg-background p-3 font-mono text-sm"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={'1 Aarav\n2 Priya\n3 Rohan'}
          />
          <Button onClick={add}>
            <Plus className="mr-1 h-4 w-4" />
            Add to roster
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Roster ({cls.learners.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {cls.learners.length === 0 ? (
            <p className="text-sm text-muted-foreground">No learners yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Roll</TableHead>
                  <TableHead>First name</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {cls.learners.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono">{l.rollNo}</TableCell>
                    <TableCell>{l.firstName}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => removeLearner(l.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
